import React, { useState } from 'react';
import { ChevronIcon } from '@clearcover/atlas';
import DiffItemComponent from './DiffItem';
import PremiumChange from './PremiumChange';
import styles from './styles.module.css';
import { DiffCategory } from './types';

interface CategorySectionProps {
  category: DiffCategory;
  isExpanded?: boolean;
  onToggle?: (key: string) => void;
}

interface CollapsibleSectionProps {
  category: DiffCategory;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  changeCount?: number;
  headerExtra?: React.ReactNode;
}

function getChangeCount(category: DiffCategory): number | undefined {
  if (category.items) {
    return category.items.length;
  }
  if (category.vehicleGroups) {
    return category.vehicleGroups.reduce(
      (sum, v) => sum + v.coverages.length + v.endorsements.length,
      0
    );
  }
  return undefined;
}

function getStringField(
  source: Record<string, unknown> | undefined,
  keys: string[]
): string | undefined {
  if (!source) {
    return undefined;
  }

  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  return undefined;
}

function getCoverageSummary(item: {
  summary: string;
  old?: Record<string, unknown>;
  new?: Record<string, unknown>;
}): string {
  const source = (item.new || item.old) as Record<string, unknown> | undefined;

  const nestedCoverage = source?.coverage as
    | Record<string, unknown>
    | undefined;

  return (
    getStringField(source, [
      'coverageLabel',
      'coverageName',
      'label',
      'displayName',
      'name',
      'coverageType',
    ]) ||
    getStringField(nestedCoverage, [
      'coverageLabel',
      'coverageName',
      'label',
      'displayName',
      'name',
      'coverageType',
    ]) ||
    item.summary
  );
}

function getEndorsementSummary(item: {
  summary: string;
  old?: Record<string, unknown>;
  new?: Record<string, unknown>;
}): string {
  const source = (item.new || item.old) as Record<string, unknown> | undefined;
  const nestedEndorsement = source?.endorsement as
    | Record<string, unknown>
    | undefined;

  return (
    getStringField(source, [
      'type',
      'endorsementType',
      'endorsementName',
      'label',
      'displayName',
      'name',
    ]) ||
    getStringField(nestedEndorsement, [
      'type',
      'endorsementType',
      'endorsementName',
      'label',
      'displayName',
      'name',
    ]) ||
    item.summary
  );
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  category,
  collapsed,
  onToggle,
  children,
  changeCount,
  headerExtra,
}) => (
  <div className={styles.section}>
    <button
      type="button"
      data-testid={`category-header-${category.key}`}
      aria-expanded={!collapsed}
      aria-label={`Toggle ${category.label} section`}
      className={styles.sectionHeader}
      onClick={onToggle}
    >
      <div className={styles.sectionTitleRow}>
        <h3 className={styles.sectionTitle}>{category.label}</h3>
        {changeCount !== undefined &&
          (changeCount > 0 ? (
            <span className={styles.sectionChangeCountPill}>
              {changeCount} {changeCount === 1 ? 'change' : 'changes'}
            </span>
          ) : (
            <span className={styles.sectionNoChanges}>No changes</span>
          ))}
        {headerExtra && (
          <span className={styles.sectionHeaderExtra}>{headerExtra}</span>
        )}
      </div>
      <ChevronIcon
        aria-hidden="true"
        style={{
          transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s',
          width: 16,
          height: 16,
        }}
      />
    </button>

    {!collapsed && <div className={styles.sectionContent}>{children}</div>}
  </div>
);

const CategorySection: React.FC<CategorySectionProps> = ({
  category,
  isExpanded,
  onToggle,
}) => {
  const [localCollapsed, setLocalCollapsed] = useState(true);
  const controlled = isExpanded !== undefined && onToggle !== undefined;
  const collapsed = controlled ? !isExpanded : localCollapsed;
  const handleToggle = controlled
    ? () => onToggle(category.key)
    : () => setLocalCollapsed((c) => !c);

  if (
    category.key === 'fullTermWrittenPremium' &&
    category.delta !== undefined
  ) {
    const oldAmount =
      ((category.old?.amount as Record<string, unknown>)?.value as
        | number
        | undefined) ?? 0;
    const newAmount =
      ((category.new?.amount as Record<string, unknown>)?.value as
        | number
        | undefined) ?? 0;

    const deltaSign = category.delta > 0 ? '+' : '';
    const deltaFormatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(category.delta);
    const deltaClass =
      category.delta > 0
        ? styles.premiumDeltaPositive
        : category.delta < 0
          ? styles.premiumDeltaNegative
          : styles.premiumDeltaNeutral;

    return (
      <CollapsibleSection
        category={category}
        collapsed={collapsed}
        onToggle={handleToggle}
        changeCount={undefined}
        headerExtra={
          <span className={deltaClass}>
            {deltaSign}
            {deltaFormatted}
          </span>
        }
      >
        <PremiumChange
          oldValue={oldAmount}
          newValue={newAmount}
          label={category.label}
          delta={category.delta}
        />
      </CollapsibleSection>
    );
  }

  if (category.key === 'vehicleCoveragesAndEndorsements') {
    return (
      <CollapsibleSection
        category={category}
        collapsed={collapsed}
        onToggle={handleToggle}
        changeCount={getChangeCount(category)}
      >
        {category.vehicleGroups?.map((vehicle) => (
          <div key={vehicle.vehicleId} className={styles.vehicleGroup}>
            <h4 className={styles.vehicleTitle}>{vehicle.vehicleLabel}</h4>

            {vehicle.coverages.length > 0 && (
              <div className={styles.vehicleSection}>
                <h5 className={styles.vehicleSectionTitle}>Coverages</h5>
                {vehicle.coverages.map((item, idx) => (
                  <DiffItemComponent
                    key={`cov-${vehicle.vehicleId}-${item.type}-${idx}`}
                    {...item}
                    summary={getCoverageSummary(item)}
                  />
                ))}
              </div>
            )}

            {vehicle.endorsements.length > 0 && (
              <div className={styles.vehicleSection}>
                <h5 className={styles.vehicleSectionTitle}>Endorsements</h5>
                {vehicle.endorsements.map((item, idx) => (
                  <DiffItemComponent
                    key={`end-${vehicle.vehicleId}-${item.type}-${idx}`}
                    {...item}
                    summary={getEndorsementSummary(item)}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </CollapsibleSection>
    );
  }

  // Standard category
  return (
    <CollapsibleSection
      category={category}
      collapsed={collapsed}
      onToggle={handleToggle}
      changeCount={getChangeCount(category)}
    >
      {category.items?.map((item, idx) => (
        <DiffItemComponent
          key={`${category.key}-${item.type}-${item.summary}-${idx}`}
          {...item}
        />
      ))}
    </CollapsibleSection>
  );
};

export default CategorySection;
